import { useEffect, useRef, useState } from 'react';
import useStyles from './styles';
import 'react-international-phone/style.css';
import { FieldInfo } from '../../FieldInfo';
import { useUserContext } from '../../../Services/Providers/UserContextProvider';
import { FormattedMessage, injectIntl } from 'react-intl';
import { FormControl, TextField } from '@material-ui/core';
import {
  defaultCountries,
  parseCountry,
  usePhoneInput,
} from 'react-international-phone';
import { PhoneNumberUtil } from 'google-libphonenumber';

const PhoneNumberInput = ({
  hidden = false,
  styles,
  customClasses,
  name,
  message,
  error,
  errorText,
  helperText,
  InputChange,
  width,
  editValue,
  value,
  placeholder,
  size,
  blur,
  title,
  focused,
  intl,
  info,
  required,
  disabled,
}) => {
  const hasMounted = useRef(false);
  const [isValid, setIsValid] = useState(true);
  const { setUserContext } = useUserContext();
  const defaultClasses = useStyles();
  const classes = !!customClasses ? customClasses() : defaultClasses;

  const phoneUtil = PhoneNumberUtil.getInstance();

  const isPhoneValid = (phone) => {
    try {
      if (phone.length === 0) return true;
      if (phone.length === 2) return true;
      return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
    } catch (error) {
      return false;
    }
  };

  const countries = defaultCountries.filter((country) => {
    const { iso2 } = parseCountry(country);
    return ['us', 'ca'].includes(iso2);
  });

  const { inputValue, handlePhoneValueChange, inputRef } = usePhoneInput({
    defaultCountry: 'ca',
    value,
    countries,
    forceDialCode: true,
    showDisabledDialCodeAndPrefix: true,
    hideDropdown: true,
    onChange: (data) => {
      if (!hasMounted.current) {
        hasMounted.current = true;
      } else {
        setUserContext({ interactedField: { name } });
        InputChange({ target: { name, value: data.phone } });
      }
    },
  });

  useEffect(() => {
    if (editValue) {
      InputChange({ target: { name, value: editValue } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (focused || !isValid) {
      inputRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focused, isValid]);

  const handleBlur = e => {
    const valid = isPhoneValid(value);
    setIsValid(valid);
    blur({...e, target: { name, value } });
  };

  const renderHelperText = () => {
    const messages = [];

    if (helperText) {
      messages.push(
        <FormattedMessage
          key="helper"
          id={helperText}
          defaultMessage={helperText}
        />
      );
    }

    if (error) {
      messages.push(
        <FormattedMessage
          key="error"
          id={errorText}
          defaultMessage={errorText}
        />
      );
    }

    if (!isValid) {
      messages.push(<FormattedMessage key="invalid" id="invalidPhoneNumber" />);
    }

    if (messages.length === 0) {
      return ' ';
    }

    return (
      <div>
        {messages.map((message, index) => (
          <div key={index}>{message}</div>
        ))}
      </div>
    );
  };
  return (
    <div
      className={classes.root}
      style={{
        position: 'relative',
        flexBasis: !!width ? width : '100%',
        display: 'flex',
      }}
      aria-live='polite'
    >
      <FormControl
        className={classes.Input}
        variant="outlined"
        style={{ flexBasis: '100%', width: '100%' }}
      >
        <TextField
          className="react-tel-input"
          variant="outlined"
          label={title ? title : placeholder}
          color="primary"
          id={name}
          value={inputValue}
          onChange={handlePhoneValueChange}
          type="tel"
          inputRef={inputRef}
          {...(size === 'small' ? { inputStyle: { height: 40 } } : {})}
          placeholder={title ? intl.formatMessage({ id: 'typeHere' }) : ''}
          onBlur={handleBlur}
          error={error || !isValid}
          style={styles}
          InputProps={{
            endAdornment: !!message && (
              <FieldInfo message={message} info={info ?? 'info'} />
            ),
          }}
          helperText={renderHelperText()}
          required={required}
          disabled={disabled}
        />
      </FormControl>
    </div>
  );
};

export default injectIntl(PhoneNumberInput);
